import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';

// MUI
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Fab } from '@mui/material';

// Icons
import { Close } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import { useDemoDealers } from 'services/Data/DealerServices';
import { useDispatch, useSelector } from 'react-redux';
import CustomAutocomplete from '@acnb/CustomComponents/CustomAutocomplete/CustomAutocomplete';
import { getHighlightParts } from 'services/Utilities/NewHighlights';
import { patchProfile } from 'redux/Account/AccountAction';
import { LanguageEnumValue } from 'enums/LanguageEnum';
import { useQueryClient } from '@tanstack/react-query';

const useStyles = makeStyles(theme => ({
  marginTop10: {
    marginTop: 10,
  },
  modalCloseButton: {
    float: 'right',
    marginTop: -6,
  },
  modalTitle: {
    minimumHeight: 50,
  },
}));

const ProfileIdentificationIsDemoEditModal = ({ show, onClose, dealerId }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const accountReducer = useSelector(state => state.AccountReducer);

  const { data: dealers } = useDemoDealers(accountReducer.isLoggedIn, accountReducer.isDemo);

  const [selectedDealerInfo, setSelectedDealerInfo] = useState();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (selectedDealerInfo?.dealerId && selectedDealerInfo?.language) {
      let attachedFileModel = {
        fieldValues: [
          {
            value: selectedDealerInfo.language === 'fr' ? LanguageEnumValue.FRENCH : LanguageEnumValue.ENGLISH,
            fieldName: 'person.languageEnum',
          },
          {
            value: selectedDealerInfo.dealerId,
            fieldName: 'temporaryDealer_Id',
          },
        ],
      };
      dispatch(patchProfile(attachedFileModel)).then(_ => {
        axios.defaults.headers.common['Accept-Language'] = selectedDealerInfo.language;
        i18n.changeLanguage(selectedDealerInfo.language);

        if (selectedDealerInfo.language === 'fr') moment.locale('fr-CA');
        else moment.locale('en-CA');

        // invalidate all query present
        queryClient.invalidateQueries();

        onClose();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, onClose, queryClient, selectedDealerInfo?.dealerId, selectedDealerInfo?.language]);

  const dealerOptions = useMemo(() => dealers || [], [dealers]);

  useEffect(() => {
    if (show) {
      const currentSelectedDealer = dealerOptions.find(d => d.dealerId === dealerId);

      if (currentSelectedDealer) {
        setSelectedDealerInfo({
          ...currentSelectedDealer,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  return (
    <Dialog
      open={show}
      maxWidth={'lg'}
      fullWidth={true}
      onClose={handleClose}
      aria-labelledby="max-width-dialog-title"
      aria-describedby="modal-slide-description">
      <DialogTitle>
        <Fab size="small" className={classes.modalCloseButton} key="close" aria-label="Close" onClick={handleClose}>
          <Close className={classes.modalClose} />
        </Fab>
        <h2 className={classes.modalTitle}>{t('edit_dealer')}</h2>
        <Divider className={classes.marginTop10} />
      </DialogTitle>
      <DialogContent id="modal-slide-description" className={classes.modalBody}>
        <Box>
          <CustomAutocomplete
            labelText={t('dealer')}
            hasSkeleton
            isLoading={false}
            options={dealerOptions}
            formControlProps={{
              fullWidth: true,
            }}
            error={false}
            autocompleteProps={{
              onChange: (event, value) => {
                if (value?.id !== selectedDealerInfo?.id) {
                  setSelectedDealerInfo({
                    ...value,
                  });
                }
              },
              isOptionEqualToValue: (dealer, value) => {
                return dealer.id === value;
              },
              value: selectedDealerInfo?.id || null,
              getOptionLabel: dealerId => {
                return dealerOptions.find(d => d.id === dealerId)?.name;
              },
              filterOptions: (dealers, state) => {
                var searchTerms = state.inputValue.toLowerCase().split(' ');
                var filteredDiscountTypes = dealers.filter(dealer =>
                  searchTerms.every(searchTerm => dealer.name?.toLowerCase().includes(searchTerm)),
                );

                return filteredDiscountTypes;
              },
              renderOption: (props, dealer, { inputValue }) => {
                const result = getHighlightParts(`${dealer.name}`, inputValue);

                return (
                  <div className={classes.autoCompleteItem} {...props}>
                    {result}
                  </div>
                );
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.modalFooter}>
        <Button
          onClick={handleConfirm}
          color="secondary"
          style={{ color: 'black', borderRadius: 0 }}
          variant="contained"
          disabled={!selectedDealerInfo?.dealerId}>
          {t('confirm')}
        </Button>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ProfileIdentificationIsDemoEditModal;
